/* MolCellQuest — 進化するアバター
   ──────────────────────────────────────────────────────────
   旅の主人公は、到達した時代に応じて姿を変える生物。
   organismInner(form, custom) は 100×100 座標系の SVG 断片を返す
   純関数で、外側の <svg> は呼び出し側が用意する。
   custom は全形態を通して一貫して反映される（色・模様・目・特徴）。
   ────────────────────────────────────────────────────────── */

/** 体色のプリセット（hue を指定する。彩度・明度は palette() が決める） */
export const HUES = [
  { id: 0, hue: 162, name: "ミント" },
  { id: 1, hue: 198, name: "シアン" },
  { id: 2, hue: 265, name: "スミレ" },
  { id: 3, hue: 32, name: "アンバー" },
  { id: 4, hue: 348, name: "コーラル" },
  { id: 5, hue: 96, name: "リーフ" },
];

export const PATTERNS = [
  { id: 0, name: "無地" },
  { id: 1, name: "斑点" },
  { id: 2, name: "縞" },
  { id: 3, name: "発光" },
];

export const EYES = [
  { id: 0, name: "まる" },
  { id: 1, name: "半目" },
  { id: 2, name: "きらり" },
  { id: 3, name: "複眼" },
];

export const ACCENTS = [
  { id: 0, name: "なし" },
  { id: 1, name: "ひれ" },
  { id: 2, name: "触角" },
  { id: 3, name: "光冠" },
];

/** 形態の並び順（進化の順序）。時代の form はこのいずれか。 */
export const FORMS = [
  "protocell",
  "bacterium",
  "eukaryote",
  "arthropod",
  "mollusk",
  "fish",
  "tetrapod",
  "amphibian",
  "synapsid",
  "reptile",
  "dinosaur",
  "bird",
  "mammal",
  "primate",
  "human",
  "posthuman",
];

export const FORM_NAMES = {
  protocell: "原始細胞",
  bacterium: "原核細胞",
  eukaryote: "真核細胞",
  arthropod: "節足動物",
  mollusk: "軟体動物",
  fish: "魚類",
  tetrapod: "四肢動物",
  amphibian: "両生類",
  synapsid: "単弓類",
  reptile: "爬虫類",
  dinosaur: "恐竜",
  bird: "鳥類",
  mammal: "哺乳類",
  primate: "霊長類",
  human: "ヒト",
  posthuman: "その先の生命",
};

export function defaultOrganism() {
  return { hue: 0, pattern: 1, eyes: 0, accent: 0 };
}

/** custom から色の一式を作る */
function palette(custom) {
  const c = custom || defaultOrganism();
  const h = (HUES[c.hue] || HUES[0]).hue;
  return {
    h,
    body: `hsl(${h} 62% 58%)`,
    dark: `hsl(${h} 58% 38%)`,
    deep: `hsl(${h} 55% 26%)`,
    light: `hsl(${h} 70% 74%)`,
    pale: `hsl(${h} 80% 88%)`,
    glow: `hsl(${h} 90% 65%)`,
    pattern: c.pattern ?? 0,
    eyes: c.eyes ?? 0,
    accent: c.accent ?? 0,
  };
}

/** 体の上に重ねる模様。cx,cy,r はおおよその体の範囲。 */
function patternOverlay(p, cx, cy, rx, ry) {
  if (p.pattern === 1) {
    // 斑点
    return (
      `<circle cx="${cx - rx * 0.35}" cy="${cy - ry * 0.2}" r="${rx * 0.16}" fill="${p.deep}" opacity=".45"/>` +
      `<circle cx="${cx + rx * 0.25}" cy="${cy + ry * 0.25}" r="${rx * 0.12}" fill="${p.deep}" opacity=".4"/>` +
      `<circle cx="${cx + rx * 0.05}" cy="${cy - ry * 0.45}" r="${rx * 0.1}" fill="${p.deep}" opacity=".35"/>`
    );
  }
  if (p.pattern === 2) {
    // 縞
    let s = "";
    for (let i = -2; i <= 2; i++) {
      s += `<rect x="${cx + i * rx * 0.38 - rx * 0.07}" y="${cy - ry}" width="${rx * 0.14}" height="${
        ry * 2
      }" fill="${p.deep}" opacity=".33" rx="2"/>`;
    }
    return `<g clip-path="url(#bodyClip)">${s}</g>`;
  }
  if (p.pattern === 3) {
    // 発光
    return `<ellipse cx="${cx}" cy="${cy}" rx="${rx * 0.62}" ry="${ry * 0.62}" fill="${p.glow}" opacity=".3"/>`;
  }
  return "";
}

/** 目。x,y に中心、r が大きさ。 */
function eye(p, x, y, r) {
  if (p.eyes === 1) {
    // 半目
    return (
      `<circle cx="${x}" cy="${y}" r="${r}" fill="#fff"/>` +
      `<path d="M${x - r} ${y} a${r} ${r} 0 0 0 ${r * 2} 0z" fill="#10201b"/>`
    );
  }
  if (p.eyes === 2) {
    // きらり
    return (
      `<circle cx="${x}" cy="${y}" r="${r}" fill="#fff"/>` +
      `<circle cx="${x}" cy="${y}" r="${r * 0.6}" fill="#10201b"/>` +
      `<circle cx="${x + r * 0.3}" cy="${y - r * 0.35}" r="${r * 0.26}" fill="#fff"/>`
    );
  }
  if (p.eyes === 3) {
    // 複眼
    let s = `<circle cx="${x}" cy="${y}" r="${r * 1.15}" fill="${p.deep}"/>`;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      s += `<circle cx="${(x + Math.cos(a) * r * 0.5).toFixed(1)}" cy="${(
        y +
        Math.sin(a) * r * 0.5
      ).toFixed(1)}" r="${r * 0.32}" fill="${p.light}" opacity=".85"/>`;
    }
    return s;
  }
  // まる
  return (
    `<circle cx="${x}" cy="${y}" r="${r}" fill="#fff"/>` +
    `<circle cx="${x}" cy="${y}" r="${r * 0.55}" fill="#10201b"/>`
  );
}

/** 特徴（ひれ・触角・光冠）。体の上下に付ける。 */
function accentDeco(p) {
  if (p.accent === 1) {
    return `<path d="M50 14 L58 4 L62 16 Z" fill="${p.light}" opacity=".9"/>`;
  }
  if (p.accent === 2) {
    return (
      `<path d="M42 22 Q36 8 28 6" stroke="${p.dark}" stroke-width="2.4" fill="none" stroke-linecap="round"/>` +
      `<circle cx="28" cy="6" r="2.6" fill="${p.light}"/>` +
      `<path d="M58 22 Q64 8 72 6" stroke="${p.dark}" stroke-width="2.4" fill="none" stroke-linecap="round"/>` +
      `<circle cx="72" cy="6" r="2.6" fill="${p.light}"/>`
    );
  }
  if (p.accent === 3) {
    return `<circle cx="50" cy="50" r="46" fill="none" stroke="${p.glow}" stroke-width="1.6" opacity=".55" stroke-dasharray="3 6"/>`;
  }
  return "";
}

/* ── 各形態 ───────────────────────────────────────────── */

const SHAPES = {
  // 原始細胞：脂質二重層に包まれた液滴
  protocell: (p) =>
    `<circle cx="50" cy="52" r="30" fill="${p.body}" opacity=".55"/>` +
    `<circle cx="50" cy="52" r="30" fill="none" stroke="${p.light}" stroke-width="4" opacity=".85"/>` +
    `<circle cx="50" cy="52" r="25" fill="none" stroke="${p.pale}" stroke-width="1.4" opacity=".5"/>` +
    patternOverlay(p, 50, 52, 30, 30) +
    `<circle cx="42" cy="46" r="4.5" fill="${p.deep}" opacity=".7"/>` +
    `<circle cx="58" cy="58" r="3.2" fill="${p.deep}" opacity=".6"/>` +
    `<circle cx="55" cy="43" r="2.4" fill="${p.deep}" opacity=".5"/>` +
    accentDeco(p),

  // 原核細胞：桿菌＋鞭毛
  bacterium: (p) =>
    `<path d="M30 66 Q14 70 8 62 Q16 60 22 62" stroke="${p.dark}" stroke-width="2.2" fill="none" stroke-linecap="round"/>` +
    `<rect x="26" y="34" width="52" height="34" rx="17" fill="${p.body}"/>` +
    patternOverlay(p, 52, 51, 26, 17) +
    `<rect x="26" y="34" width="52" height="34" rx="17" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M36 44 Q48 40 62 46" stroke="${p.pale}" stroke-width="2" fill="none" opacity=".5" stroke-linecap="round"/>` +
    eye(p, 66, 48, 5) +
    accentDeco(p),

  // 真核細胞：核とミトコンドリア
  eukaryote: (p) =>
    `<circle cx="50" cy="52" r="33" fill="${p.body}" opacity=".8"/>` +
    patternOverlay(p, 50, 52, 33, 33) +
    `<circle cx="50" cy="52" r="33" fill="none" stroke="${p.dark}" stroke-width="2.4"/>` +
    `<circle cx="46" cy="48" r="13" fill="${p.deep}" opacity=".85"/>` +
    `<circle cx="46" cy="48" r="13" fill="none" stroke="${p.pale}" stroke-width="1.4" opacity=".6"/>` +
    `<circle cx="46" cy="48" r="4" fill="${p.pale}" opacity=".7"/>` +
    `<ellipse cx="66" cy="64" rx="9" ry="5" fill="${p.light}" transform="rotate(-25 66 64)"/>` +
    `<ellipse cx="32" cy="68" rx="7" ry="4" fill="${p.light}" transform="rotate(20 32 68)"/>` +
    eye(p, 68, 40, 4.5) +
    accentDeco(p),

  // 節足動物：三葉虫
  arthropod: (p) => {
    let seg = "";
    for (let i = 0; i < 5; i++) {
      seg += `<rect x="26" y="${44 + i * 9}" width="48" height="7" rx="3.5" fill="${p.dark}" opacity=".55"/>`;
    }
    return (
      `<path d="M50 20 Q80 26 80 56 Q80 84 50 88 Q20 84 20 56 Q20 26 50 20Z" fill="${p.body}"/>` +
      seg +
      patternOverlay(p, 50, 54, 30, 34) +
      `<path d="M50 20 Q80 26 80 56 Q80 84 50 88 Q20 84 20 56 Q20 26 50 20Z" fill="none" stroke="${p.deep}" stroke-width="2.2"/>` +
      `<path d="M36 22 Q34 10 28 6" stroke="${p.dark}" stroke-width="2" fill="none" stroke-linecap="round"/>` +
      `<path d="M64 22 Q66 10 72 6" stroke="${p.dark}" stroke-width="2" fill="none" stroke-linecap="round"/>` +
      eye(p, 40, 34, 5) +
      eye(p, 60, 34, 5) +
      accentDeco(p)
    );
  },

  // 軟体動物：オウムガイ
  mollusk: (p) => {
    // 対数螺旋を折れ線で近似する
    let d = "";
    for (let i = 0; i <= 42; i++) {
      const t = i / 42;
      const a = t * Math.PI * 3.4;
      const r = 4 + 30 * Math.pow(t, 1.35);
      const x = 46 + Math.cos(a) * r;
      const y = 50 - Math.sin(a) * r;
      d += (i === 0 ? "M" : "L") + x.toFixed(1) + " " + y.toFixed(1);
    }
    return (
      `<circle cx="46" cy="50" r="33" fill="${p.body}" opacity=".9"/>` +
      patternOverlay(p, 46, 50, 33, 33) +
      `<circle cx="46" cy="50" r="33" fill="none" stroke="${p.deep}" stroke-width="2.2"/>` +
      `<path d="${d}" fill="none" stroke="${p.deep}" stroke-width="2" opacity=".75"/>` +
      `<path d="M74 62 q10 4 16 0 M74 68 q10 5 15 2 M73 56 q11 1 17 -3" stroke="${p.dark}" stroke-width="2.4" fill="none" stroke-linecap="round"/>` +
      eye(p, 72, 48, 4.5) +
      accentDeco(p)
    );
  },

  // 魚類
  fish: (p) =>
    `<path d="M18 50 L34 36 L34 64 Z" fill="${p.dark}"/>` +
    `<path d="M34 50 Q46 26 66 30 Q86 34 90 50 Q86 66 66 70 Q46 74 34 50Z" fill="${p.body}"/>` +
    patternOverlay(p, 60, 50, 26, 20) +
    `<path d="M34 50 Q46 26 66 30 Q86 34 90 50 Q86 66 66 70 Q46 74 34 50Z" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M58 30 L62 16 L70 32 Z" fill="${p.light}"/>` +
    `<path d="M56 66 L58 78 L68 68 Z" fill="${p.light}"/>` +
    `<path d="M52 46 q8 6 0 12" stroke="${p.deep}" stroke-width="1.8" fill="none" opacity=".6"/>` +
    eye(p, 78, 45, 4.5) +
    accentDeco(p),

  // 四肢動物：ひれから足へ
  tetrapod: (p) =>
    `<path d="M14 58 Q8 52 10 46 Q16 50 22 52 Z" fill="${p.dark}"/>` +
    `<ellipse cx="52" cy="50" rx="32" ry="17" fill="${p.body}"/>` +
    patternOverlay(p, 52, 50, 32, 17) +
    `<ellipse cx="52" cy="50" rx="32" ry="17" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M34 64 l-5 14 M44 66 l-2 14 M64 66 l3 14 M74 62 l7 13" stroke="${p.dark}" stroke-width="4" stroke-linecap="round"/>` +
    `<ellipse cx="80" cy="44" rx="13" ry="10" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    eye(p, 84, 41, 4) +
    accentDeco(p),

  // 両生類
  amphibian: (p) =>
    `<ellipse cx="50" cy="56" rx="30" ry="20" fill="${p.body}"/>` +
    patternOverlay(p, 50, 56, 30, 20) +
    `<ellipse cx="50" cy="56" rx="30" ry="20" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<ellipse cx="50" cy="38" rx="22" ry="14" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M24 68 q-10 6 -12 14 M76 68 q10 6 12 14 M32 72 l-4 12 M68 72 l4 12" stroke="${p.dark}" stroke-width="4" stroke-linecap="round" fill="none"/>` +
    eye(p, 40, 32, 5.5) +
    eye(p, 60, 32, 5.5) +
    `<path d="M42 44 q8 4 16 0" stroke="${p.deep}" stroke-width="1.8" fill="none" stroke-linecap="round"/>` +
    accentDeco(p),

  // 単弓類：背の帆
  synapsid: (p) => {
    let sail = `<path d="M30 44 Q50 4 74 42 Z" fill="${p.light}" opacity=".85"/>`;
    for (let i = 1; i < 5; i++) {
      const x = 32 + i * 8;
      sail += `<line x1="${x}" y1="44" x2="${x + 2}" y2="${16 + Math.abs(i - 2.5) * 7}" stroke="${
        p.deep
      }" stroke-width="1.6" opacity=".6"/>`;
    }
    return (
      sail +
      `<ellipse cx="50" cy="56" rx="31" ry="16" fill="${p.body}"/>` +
      patternOverlay(p, 50, 56, 31, 16) +
      `<ellipse cx="50" cy="56" rx="31" ry="16" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
      `<path d="M30 68 l-4 14 M42 70 l-1 14 M60 70 l2 14 M72 66 l6 14" stroke="${p.dark}" stroke-width="4" stroke-linecap="round"/>` +
      `<path d="M81 52 q14 2 16 8 q-9 3 -16 0Z" fill="${p.body}" stroke="${p.dark}" stroke-width="1.8"/>` +
      eye(p, 88, 54, 3.6) +
      accentDeco(p)
    );
  },

  // 爬虫類：トカゲ
  reptile: (p) =>
    `<path d="M16 62 Q4 60 6 52" stroke="${p.dark}" stroke-width="5" fill="none" stroke-linecap="round"/>` +
    `<ellipse cx="50" cy="56" rx="32" ry="14" fill="${p.body}"/>` +
    patternOverlay(p, 50, 56, 32, 14) +
    `<ellipse cx="50" cy="56" rx="32" ry="14" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M22 48 l4 -6 M34 45 l3 -6 M48 44 l2 -6 M62 45 l3 -6" stroke="${p.deep}" stroke-width="2.6" stroke-linecap="round"/>` +
    `<path d="M30 68 l-6 12 M44 70 l-2 12 M60 70 l3 12 M74 66 l7 12" stroke="${p.dark}" stroke-width="4" stroke-linecap="round"/>` +
    `<ellipse cx="84" cy="50" rx="14" ry="9" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    eye(p, 88, 47, 4) +
    accentDeco(p),

  // 恐竜：竜脚類
  dinosaur: (p) =>
    `<path d="M22 74 Q6 70 2 60" stroke="${p.dark}" stroke-width="6" fill="none" stroke-linecap="round"/>` +
    `<ellipse cx="46" cy="64" rx="28" ry="18" fill="${p.body}"/>` +
    patternOverlay(p, 46, 64, 28, 18) +
    `<ellipse cx="46" cy="64" rx="28" ry="18" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M34 80 l-3 14 M48 82 l0 13 M60 80 l4 14" stroke="${p.dark}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M66 56 Q84 44 82 22" stroke="${p.body}" stroke-width="11" fill="none" stroke-linecap="round"/>` +
    `<path d="M66 56 Q84 44 82 22" stroke="${p.dark}" stroke-width="2" fill="none" opacity=".4"/>` +
    `<ellipse cx="82" cy="18" rx="10" ry="7" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    eye(p, 86, 16, 3.4) +
    accentDeco(p),

  // 鳥類
  bird: (p) =>
    `<path d="M24 62 l-16 8 l14 4 Z" fill="${p.light}"/>` +
    `<ellipse cx="50" cy="56" rx="26" ry="20" fill="${p.body}"/>` +
    patternOverlay(p, 50, 56, 26, 20) +
    `<ellipse cx="50" cy="56" rx="26" ry="20" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M40 50 Q56 40 72 54 Q56 62 40 50Z" fill="${p.light}" stroke="${p.dark}" stroke-width="1.6"/>` +
    `<circle cx="66" cy="30" r="13" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M78 30 l12 4 l-12 4Z" fill="${p.pale}" stroke="${p.dark}" stroke-width="1.4"/>` +
    `<path d="M44 74 l-2 12 M58 74 l2 12" stroke="${p.dark}" stroke-width="3.4" stroke-linecap="round"/>` +
    eye(p, 71, 27, 4) +
    accentDeco(p),

  // 哺乳類
  mammal: (p) =>
    `<path d="M22 60 Q6 56 8 42" stroke="${p.dark}" stroke-width="4.5" fill="none" stroke-linecap="round"/>` +
    `<ellipse cx="48" cy="58" rx="28" ry="19" fill="${p.body}"/>` +
    patternOverlay(p, 48, 58, 28, 19) +
    `<ellipse cx="48" cy="58" rx="28" ry="19" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M34 74 l-3 13 M46 76 l0 12 M60 74 l3 13 M70 70 l6 12" stroke="${p.dark}" stroke-width="4" stroke-linecap="round"/>` +
    `<circle cx="74" cy="38" r="15" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M64 26 l-3 -11 l11 5Z" fill="${p.light}" stroke="${p.dark}" stroke-width="1.4"/>` +
    `<path d="M84 26 l4 -11 l-11 5Z" fill="${p.light}" stroke="${p.dark}" stroke-width="1.4"/>` +
    `<circle cx="84" cy="42" r="2.6" fill="${p.deep}"/>` +
    eye(p, 76, 36, 4) +
    accentDeco(p),

  // 霊長類
  primate: (p) =>
    `<ellipse cx="50" cy="66" rx="24" ry="22" fill="${p.body}"/>` +
    patternOverlay(p, 50, 66, 24, 22) +
    `<ellipse cx="50" cy="66" rx="24" ry="22" fill="none" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M28 60 q-12 12 -8 24 M72 60 q12 12 8 24" stroke="${p.dark}" stroke-width="5" fill="none" stroke-linecap="round"/>` +
    `<circle cx="50" cy="32" r="18" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    `<ellipse cx="50" cy="38" rx="9" ry="7" fill="${p.pale}" opacity=".75"/>` +
    `<circle cx="30" cy="30" r="5.5" fill="${p.body}" stroke="${p.dark}" stroke-width="1.8"/>` +
    `<circle cx="70" cy="30" r="5.5" fill="${p.body}" stroke="${p.dark}" stroke-width="1.8"/>` +
    eye(p, 43, 28, 4.2) +
    eye(p, 57, 28, 4.2) +
    accentDeco(p),

  // ヒト
  human: (p) =>
    `<circle cx="50" cy="24" r="14" fill="${p.body}" stroke="${p.dark}" stroke-width="2"/>` +
    `<path d="M50 38 L50 68" stroke="${p.body}" stroke-width="16" stroke-linecap="round"/>` +
    `<path d="M50 38 L50 68" stroke="${p.dark}" stroke-width="2" opacity=".25"/>` +
    patternOverlay(p, 50, 52, 12, 16) +
    `<path d="M42 44 L26 58 M58 44 L74 58" stroke="${p.body}" stroke-width="7" stroke-linecap="round"/>` +
    `<path d="M45 66 L38 90 M55 66 L62 90" stroke="${p.body}" stroke-width="8" stroke-linecap="round"/>` +
    `<path d="M45 66 L38 90 M55 66 L62 90" stroke="${p.dark}" stroke-width="1.6" opacity=".2"/>` +
    eye(p, 44, 22, 3.6) +
    eye(p, 56, 22, 3.6) +
    accentDeco(p),

  // その先の生命
  posthuman: (p) => {
    // 二重らせんのオーラ
    let helix = "";
    for (let i = 0; i <= 16; i++) {
      const t = i / 16;
      const y = 8 + t * 84;
      const x1 = 50 + Math.sin(t * Math.PI * 3) * 30;
      const x2 = 50 - Math.sin(t * Math.PI * 3) * 30;
      if (i % 2 === 0) {
        helix += `<line x1="${x1.toFixed(1)}" y1="${y.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y.toFixed(
          1
        )}" stroke="${p.glow}" stroke-width="1.2" opacity=".35"/>`;
      }
      helix += `<circle cx="${x1.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${p.glow}" opacity=".7"/>`;
      helix += `<circle cx="${x2.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${p.accent2 || p.light}" opacity=".7"/>`;
    }
    return (
      `<g class="jflicker">${helix}</g>` +
      `<circle cx="50" cy="26" r="13" fill="${p.body}" stroke="${p.pale}" stroke-width="2"/>` +
      `<path d="M50 39 L50 66" stroke="${p.body}" stroke-width="15" stroke-linecap="round"/>` +
      `<path d="M43 45 L30 57 M57 45 L70 57" stroke="${p.body}" stroke-width="6.5" stroke-linecap="round"/>` +
      `<path d="M46 64 L40 88 M54 64 L60 88" stroke="${p.body}" stroke-width="7.5" stroke-linecap="round"/>` +
      `<circle cx="50" cy="50" r="30" fill="none" stroke="${p.glow}" stroke-width="1.4" opacity=".4"/>` +
      eye(p, 45, 24, 3.4) +
      eye(p, 55, 24, 3.4)
    );
  },
};

/**
 * 100×100 座標系の SVG 断片を返す。
 * @param {string} form  FORMS のいずれか
 * @param {object} custom { hue, pattern, eyes, accent }
 */
export function organismInner(form, custom) {
  const p = palette(custom);
  const shape = SHAPES[form] || SHAPES.protocell;
  // 縞模様のクリップ用に体の輪郭をひとつ用意しておく
  const defs =
    p.pattern === 2
      ? `<defs><clipPath id="bodyClip"><ellipse cx="50" cy="52" rx="33" ry="26"/></clipPath></defs>`
      : "";
  return defs + shape(p);
}

/** 単体で使える <svg> 文字列 */
export function organismSvg(form, custom, size = 64) {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}" ` +
    `role="img" aria-label="${FORM_NAMES[form] || "生物"}">${organismInner(form, custom)}</svg>`
  );
}
